
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import * as React from 'react';
import { GetStaticProps, NextPage } from 'next';
import Stack from '@mui/material/Stack';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import { I18n, useTranslation } from 'components/hooks/use-translation';
import Collection, { CollectionItem } from 'components/atomic/molecules/collection';
import SideBarLayout from 'components/layouts/sidebar-layout';
import SearchBar from 'pages/paths/search-bar';
import Button from 'components/atomic/atoms/button';
import Text from 'components/atomic/atoms/text';
import Routes from 'utils/routes';
import SEO from 'pages/catalog/seo';
import { Difficulty, PathGoal, PathOverview } from 'types/types';
import devvSdk from 'utils/devv-sdk';
interface CatalogPageProps {
    items: CollectionItem[];
    sideBarRoutes: Routes;
    fingerprints: Record<string, string>;
}
const CatalogPage: NextPage<CatalogPageProps> = (props) => {
    const [sideBarOpen, setSideBarOpen] = React.useState(false);
    const [isFiltering, setIsFiltering] = React.useState(false);
    const [filters, setFilters] = React.useState<{
        search: string;
        difficulty: Difficulty | '';
        type: PathGoal | '';
    }>({
        search: '',
        difficulty: '',
        type: '',
    });
    const { t }: I18n = useTranslation();
    React.useEffect(() => {
        setIsFiltering(!!filters.difficulty || !!filters.search || !!filters.type);
    }, [filters]);
    const filteredItems = React.useMemo(() => {
        return props.items.filter(({ type, item }) => {
            const difficultyMatch = !!filters.difficulty ? filters.difficulty === item.difficulty : true;
            const typeMatch = !!filters.type
                ? filters.type === type || filters.type === (item as PathOverview).goal
                : true;
            const searchMatch = !!filters.search
                ? props.fingerprints[item._id].includes(filters.search.toLowerCase().trim())
                : true;
            return difficultyMatch && typeMatch && searchMatch;
        });
    }, [props.items, props.fingerprints, filters]);
    return (<>
            <SEO title={t('catalog:available.title')} description={t('catalog:available.subtitle')} items={props.items} breadcrumbs={[
            { name: 'Catalogo', path: Routes.Catalog },
            { name: 'Disponibili ora', path: Routes.CatalogAvailable },
        ]} imageName="catalog"/>
            <SideBarLayout sideBarRoutes={props.sideBarRoutes} sideBarMobileOpen={sideBarOpen} onSideBarClose={() => setSideBarOpen(false)}>
                <Stack gap="2rem">
                    {/** Title */}
                    <Stack spacing="1rem">
                        <Text variant="h3">{t('catalog:available.title')}</Text>
                        <Text variant="subtitle1">{t('catalog:available.subtitle')}</Text>
                        <div>
                            <Button sx={{ display: { md: 'none' } }} onClick={() => setSideBarOpen(true)} startIcon={<MenuRoundedIcon />}>
                                {t('catalog:buttons.openMenu')}
                            </Button>
                        </div>
                    </Stack>

                    {/** Search */}
                    <SearchBar onChange={(value) => setFilters(value)} filters={filters}/>

                    {isFiltering ? (<Collection key="filtered" title={`${filteredItems.length} risultati`} items={filteredItems} limit={6}/>) : (<Collection key="all-courses" items={props.items} limit={9}/>)}
                </Stack>
            </SideBarLayout>
        </>);
};
const getStaticProps: GetStaticProps<CatalogPageProps> = async () => {
    const { data: paths } = await devvSdk.getPathsOverview({ live: true });
    const { data: courses } = await devvSdk.getCoursesOverview({ live: true });
    const items = [
        ...paths.map((e) => ({ type: 'path', item: e })),
        ...courses.map((e) => ({ type: 'course', item: e })),
    ] as CollectionItem[];
    // Creates a fingerprint for each path to be used in the search
    const fingerprints = items.reduce((acc, { item }) => {
        acc[item._id] = `${item.title}${item.short_description}${item.categories
            .map((e) => e.title)
            .join('')}`.toLowerCase();
        return acc;
    }, {} as Record<string, string>);
    // const categoriesResponse = await devvSdk.getCategories()
    const sideBarRoutes = [
        {
            rootLabel: 'Linguaggi',
            routes: [
                {
                    to: Routes.Category('python'),
                    label: 'Python',
                },
                {
                    to: Routes.Category('html-css'),
                    label: 'HTML & CSS',
                },
                {
                    to: Routes.Category('javascript'),
                    label: 'JavaScript',
                },
                {
                    to: Routes.Category('sql'),
                    label: 'SQL',
                },
                {
                    to: Routes.Category('java'),
                    label: 'Java',
                },
                {
                    to: Routes.Category('r'),
                    label: 'R',
                },
                {
                    to: Routes.Category('c-sharp'),
                    label: 'C#',
                },
                {
                    to: Routes.Category('swift'),
                    label: 'Swift',
                },
                {
                    to: Routes.Category('bash'),
                    label: 'Bash/Shell',
                },
            ],
        },
        {
            rootLabel: 'Argomenti',
            routes: [
                {
                    to: Routes.Category('web-development'),
                    label: 'Web Development',
                },
                {
                    to: Routes.Category('game-development'),
                    label: 'Game Development',
                },
                {
                    to: Routes.Category('mobile-development'),
                    label: 'Mobile Development',
                },
                {
                    to: Routes.Category('cybersecurity'),
                    label: 'Cybersecurity',
                },
                {
                    to: Routes.Category('machine-learning'),
                    label: 'Machine Learning',
                },
                {
                    to: Routes.Category('data-visualization'),
                    label: 'Data Visualization',
                },
                {
                    to: Routes.Category('web-design'),
                    label: 'Web Design',
                },
                {
                    to: Routes.Category('data-science'),
                    label: 'Data Science',
                },
                {
                    to: Routes.Category('artificial-intelligence'),
                    label: 'Artificial Intelligence (AI)',
                },
                {
                    to: Routes.Category('data-engineering'),
                    label: 'Data Engineering',
                },
                {
                    to: Routes.Category('data-analytics'),
                    label: 'Data Analytics',
                },
                {
                    to: Routes.Category('ios'),
                    label: 'iOS Development',
                },
                {
                    to: Routes.Category('math'),
                    label: 'Matematica',
                },
            ],
        },
        {
            rootLabel: 'Mondo del lavoro',
            routes: [
                {
                    to: Routes.Category('job-essentials'),
                    label: 'Impara una carriera',
                },
                {
                    to: Routes.Category('interview-prep'),
                    label: 'Preparazione colloquio',
                },
            ],
        },
        {
            rootLabel: 'Ruolo',
            routes: [
                {
                    to: Routes.Category('marketing'),
                    label: 'Marketing',
                },
                {
                    to: Routes.Category('product-management'),
                    label: 'Product Management',
                },
                {
                    to: Routes.Category('consulting'),
                    label: 'Consulenza',
                },
                {
                    to: Routes.Category('data-analyst'),
                    label: 'Data Analyst',
                },
                {
                    to: Routes.Category('data-scientist'),
                    label: 'Data Scientist',
                },
                {
                    to: Routes.Category('front-end-engineering'),
                    label: 'Front-End Engineer',
                },
                {
                    to: Routes.Category('back-end-engineering'),
                    label: 'Back-End Engineer',
                },
                {
                    to: Routes.Category('full-stack-engineering'),
                    label: 'Full-Stack Engineer',
                },
                {
                    to: Routes.Category('technical-recruiter'),
                    label: 'Technical Recruiter',
                },
                {
                    to: Routes.Category('ios-developer'),
                    label: 'iOS Developer',
                },
                {
                    to: Routes.Category('android-developer'),
                    label: 'Android Developer',
                },
                {
                    to: Routes.Category('sales'),
                    label: 'Sales',
                },
                {
                    to: Routes.Category('operations'),
                    label: 'Ammministrazione',
                },
                {
                    to: Routes.Category('seo-specialist'),
                    label: 'SEO Specialist',
                },
                {
                    to: Routes.Category('product-marketing-management'),
                    label: 'Product Marketing Management',
                },
                {
                    to: Routes.Category('finance'),
                    label: 'Finanza',
                },
                {
                    to: Routes.Category('business-development'),
                    label: 'Business Development',
                },
                {
                    to: Routes.Category('business-intelligence-analyst'),
                    label: 'Business Intelligence Analyst',
                },
            ],
        },
    ] as Routes;
    return {
        props: {
            items,
            fingerprints,
            sideBarRoutes,
        },
    };
};
export default CatalogPage;

    async function __Next_Translate__getStaticProps__18916ceb279__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/catalog/available',
            loaderName: 'getStaticProps',
            loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__18916ceb279__ as getStaticProps }
  